import { browserTimers } from "./types.js";
export class TableAudioClient {
    transport;
    timer;
    pushToTalkLimitMs;
    onHostMuteRequest;
    listeners = new Set();
    pushToTalkTimer = null;
    state = {
        connected: false,
        voiceMode: "push-to-talk",
        pushToTalkActive: false,
        speakerEnabled: true,
        tableMuted: false,
        networkQuality: "unknown",
        speakingParticipantIds: [],
        permissionError: null
    };
    constructor(options) {
        this.transport = options.transport;
        this.timer = options.timer ?? browserTimers;
        this.pushToTalkLimitMs = options.pushToTalkLimitMs ?? 15_000;
        this.onHostMuteRequest = options.onHostMuteRequest ?? null;
        if (this.pushToTalkLimitMs <= 0 || this.pushToTalkLimitMs > 15_000)
            throw new Error("Push-to-talk duration must be between 1 and 15000 ms");
    }
    snapshot() {
        return { ...this.state, speakingParticipantIds: [...this.state.speakingParticipantIds] };
    }
    subscribe(listener) {
        this.listeners.add(listener);
        listener(this.snapshot());
        return () => this.listeners.delete(listener);
    }
    async connect(grant) {
        if (grant.expiresAt <= Date.now())
            throw new Error("Audio grant expired");
        if (this.state.connected)
            await this.disconnect();
        await this.transport.connect(grant, this.transportEvents());
        this.update({ connected: true, permissionError: null });
        await this.applyMicrophonePolicy();
        await this.transport.setSpeakerEnabled(this.state.speakerEnabled);
    }
    async setVoiceMode(mode) {
        this.stopPushToTalkTimer();
        this.update({ voiceMode: mode, pushToTalkActive: false });
        await this.applyMicrophonePolicy();
    }
    async beginPushToTalk() {
        if (!this.state.connected || this.state.voiceMode !== "push-to-talk" || this.state.tableMuted)
            return;
        this.stopPushToTalkTimer();
        this.update({ pushToTalkActive: true });
        await this.transport.setMicrophoneEnabled(true);
        this.pushToTalkTimer = this.timer.setTimeout(() => { void this.endPushToTalk(); }, this.pushToTalkLimitMs);
    }
    async endPushToTalk() {
        this.stopPushToTalkTimer();
        if (!this.state.pushToTalkActive)
            return;
        this.update({ pushToTalkActive: false });
        await this.transport.setMicrophoneEnabled(false);
    }
    async setSpeakerEnabled(enabled) {
        this.update({ speakerEnabled: enabled });
        if (this.state.connected)
            await this.transport.setSpeakerEnabled(enabled);
    }
    async requestHostTableMute(muted) {
        if (!this.onHostMuteRequest)
            throw new Error("Host table mute requires a server action callback");
        await this.onHostMuteRequest(muted);
    }
    async applyTableMuted(muted) {
        this.stopPushToTalkTimer();
        this.update({ tableMuted: muted, pushToTalkActive: false });
        await this.applyMicrophonePolicy();
    }
    async disconnect() {
        this.stopPushToTalkTimer();
        try {
            await this.transport.setMicrophoneEnabled(false);
        }
        finally {
            await this.transport.disconnect();
            this.update({
                connected: false,
                pushToTalkActive: false,
                networkQuality: "unknown",
                speakingParticipantIds: []
            });
        }
    }
    async dispose() {
        await this.disconnect();
        this.listeners.clear();
    }
    async applyMicrophonePolicy() {
        if (!this.state.connected)
            return;
        const enabled = !this.state.tableMuted && (this.state.voiceMode === "open-mic" || (this.state.voiceMode === "push-to-talk" && this.state.pushToTalkActive));
        await this.transport.setMicrophoneEnabled(enabled);
    }
    transportEvents() {
        return {
            onSpeakingChanged: (participantId, speaking) => {
                const participants = new Set(this.state.speakingParticipantIds);
                if (speaking)
                    participants.add(participantId);
                else
                    participants.delete(participantId);
                this.update({ speakingParticipantIds: [...participants] });
            },
            onNetworkQualityChanged: (networkQuality) => this.update({ networkQuality }),
            onPermissionError: (error) => {
                this.stopPushToTalkTimer();
                this.update({ permissionError: error.message, pushToTalkActive: false });
            },
            onDisconnected: () => {
                this.stopPushToTalkTimer();
                this.update({ connected: false, pushToTalkActive: false, speakingParticipantIds: [], networkQuality: "lost" });
            }
        };
    }
    stopPushToTalkTimer() {
        if (this.pushToTalkTimer !== null)
            this.timer.clearTimeout(this.pushToTalkTimer);
        this.pushToTalkTimer = null;
    }
    update(patch) {
        this.state = { ...this.state, ...patch };
        for (const listener of this.listeners)
            listener(this.snapshot());
    }
}
//# sourceMappingURL=table-audio-client.js.map